import { PantallaCargando } from '@/components/Cargando';
import EmailVisualEditor from '@/components/email-editor/EmailVisualEditor';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import HelpTooltip from '@/components/help/HelpTooltip';
import SesDominioPanel from '@/components/email/SesDominioPanel';
import {
  Mail, Plus, Pencil, Trash2, Eye, Send, Save, X, ArrowLeft,
  FileText, Search, ChevronRight, AlertTriangle, CheckCircle
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-amber-50 text-amber-600',
  active: 'bg-emerald-50 text-emerald-600',
  archived: 'bg-gray-100 text-gray-500',
};
const VAR_HINTS = ['nombre_cliente', 'tenant_name', 'logo_url', 'enlace', 'fecha_cita', 'business_name', 'contact_name', 'contact_email', 'current_date'];
const EMAIL_PROVIDER_NAMES = {
  smtp: 'SMTP', smtp_custom: 'SMTP', sendgrid: 'SendGrid', resend: 'Resend',
  mailgun: 'Mailgun', ses: 'Amazon SES', brevo: 'Brevo',
};
const friendlyEmailProviderName = (key) => EMAIL_PROVIDER_NAMES[key] || key;

/** S87 — cada diseño de galería puede activar un envío automático. */
const STOCK_AUTO = {
  stock_cumple: { recipeId: 'bday_hello', label: 'Cumpleaños automático', path: '/tenant/email/automaticos?recipe=bday_hello' },
  stock_cita: { recipeId: 'appt_remind', label: 'Recordatorio de cita', path: '/tenant/email/automaticos?recipe=appt_remind' },
  stock_bienvenida: { recipeId: 'welcome', label: 'Bienvenida al lead', path: '/tenant/email/automaticos?recipe=welcome' },
  stock_promo: { recipeId: 'promo_month', label: 'Promo manual', path: '/tenant/email/automaticos?recipe=promo_month' },
  stock_reactivacion: { recipeId: 'reactivate', label: 'Reactivar inactivos', path: '/tenant/email/automaticos?recipe=reactivate' },
  stock_ano_nuevo: { recipeId: 'new_year', label: 'Saludo de temporada', path: '/tenant/email/automaticos?recipe=new_year' },
  stock_black_friday: { recipeId: 'promo_month', label: 'Promo / campaña', path: '/tenant/email/automaticos?recipe=promo_month' },
};


/** Plantillas sembradas por el CRM / sprints viejos — son EMAIL, no WhatsApp. */
const SYSTEM_EMAIL_KEYS = new Set([
  'Welcome', 'bienvenida', 'promocion_mensual', 'recordatorio_cita', 'confirmacion_cita',
  'crm_task_activity_notice_seller', 'crm_appointment_confirm',
]);
const SYSTEM_NAME_HINTS = [
  'bienvenida cliente', 'bienvenida a nueva clienta', 'promoción del mes', 'promocion del mes',
  'recordatorio de cita', 'confirmación de cita', 'confirmacion de cita',
];

function isSystemEmailTemplate(t) {
  if (!t) return false;
  if (t.design_json) return false;
  const key = (t.key || '').trim();
  const name = (t.name || '').trim().toLowerCase();
  if (SYSTEM_EMAIL_KEYS.has(key) || key.startsWith('crm_')) return true;
  return SYSTEM_NAME_HINTS.some(h => name.includes(h));
}

function templateOrigin(t) {
  if (t.design_json) return 'visual';
  if (isSystemEmailTemplate(t)) return 'system';
  return 'html';
}

function OriginBadge({ t }) {
  const o = templateOrigin(t);
  if (o === 'visual') {
    return (
      <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFF5F9] text-[#F72585] border border-[#F72585]/30 font-semibold" data-testid="badge-diseno-visual">
        Diseño visual
      </span>
    );
  }
  if (o === 'system') {
    return (
      <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold" data-testid="badge-sistema">
        Sistema · email
      </span>
    );
  }
  return (
    <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 font-semibold" data-testid="badge-html">
      HTML
    </span>
  );
}


export default function EmailTemplates({ forcedTab, onTabChange, marketingEmbed } = {}) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(forcedTab || 'templates');
  const [stock, setStock] = useState([]);
  const [cloning, setCloning] = useState('');
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testModal, setTestModal] = useState(null);
  const [filter, setFilter] = useState(''); // '' | draft | active | archived
  const [search, setSearch] = useState('');

  const reloadTemplates = async (statusFilter = filter) => {
    const qs = statusFilter === 'archived'
      ? '?status=archived'
      : (statusFilter === 'draft' || statusFilter === 'active')
        ? `?status=${statusFilter}`
        : '';
    const tRes = await api.get(`/tenant/email/templates${qs}`);
    setTemplates(tRes.data || []);
    return tRes.data || [];
  };

  useEffect(() => {
    const load = async () => {
      try {
        try { await api.post('/tenant/email/templates/purge-system'); } catch { /* ok */ }
        const [tRes, cRes, lRes] = await Promise.all([
          api.get('/tenant/email/templates'),
          api.get('/tenant/email/config').catch(() => ({ data: {} })),
          api.get('/tenant/email/logs?limit=10').catch(() => ({ data: [] })),
        ]);
        setTemplates(tRes.data || []);
        setConfig(cRes.data || {});
        setLogs(lRes.data || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    reloadTemplates(filter).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);


  useEffect(() => {
    if (forcedTab && forcedTab !== tab) setTab(forcedTab);
  }, [forcedTab]);

  useEffect(() => {
    if (tab !== 'templates') return;
    api.get('/tenant/email/stock-templates').then(r => setStock(r.data?.templates || [])).catch(() => {});
  }, [tab]);

  const findExistingStockCopy = (stockItem) => {
    const base = (stockItem.key || '').replace(/^stock_/, '');
    return templates.find(x => {
      if (x.status === 'archived') return false;
      if (x.design_json?.meta?.stock_key === stockItem.key) return true;
      if (x.key && base && x.key.startsWith(`${base}-`) && x.design_json) return true;
      if (x.name === stockItem.name && x.design_json) return true;
      return false;
    });
  };

  const openStock = async (stockItem) => {
    const existing = findExistingStockCopy(stockItem);
    if (existing) {
      toast.message(`Abriendo «${existing.name}» (ya era tuya)`);
      setEditing(existing);
      return;
    }
    setCloning(stockItem.key);
    try {
      const { data } = await api.post(`/tenant/email/stock-templates/${stockItem.key}/clone`);
      toast.success(data.reused
        ? `Abriendo «${data.name}» (ya era tuya)`
        : `«${data.name}» lista para editar`);
      setEditing(data);
      if (filter !== 'archived') {
        await reloadTemplates(filter);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCloning('');
    }
  };

  if (loading) return <PantallaCargando />;

  if (editing) return <EmailVisualEditor template={editing === 'new' ? null : editing} onBack={() => setEditing(null)} onSaved={(t) => { setTemplates(prev => { const i = prev.findIndex(x => x.id === t.id); if (i >= 0) { const n=[...prev]; n[i]=t; return n; } return [t, ...prev]; }); }} />;
  if (preview) return <PreviewPanel template={preview} onBack={() => setPreview(null)} />;
  if (testModal) return <TestSendPanel template={testModal} onBack={() => setTestModal(null)} />;

  const filtered = templates.filter(t => {
    if (search && !(t.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filter !== 'archived' && isSystemEmailTemplate(t)) return false;
    if (filter === 'draft' || filter === 'active' || filter === 'archived') return t.status === filter;
    return t.status !== 'archived';
  });

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      {!marketingEmbed && (
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2"><Mail className="w-6 h-6" /> Email</h1>
          <p className="text-sm text-[#64748B] mt-1">Mailing masivo: plantillas, dominio y pruebas</p>
        </div>
        <Button onClick={() => setEditing('new')} className="bg-[#0F172A] text-white text-xs gap-1" data-testid="create-template-btn"><Plus className="w-3.5 h-3.5" /> Nueva plantilla</Button>
      </div>
      )}
      {marketingEmbed && (
        <div className="flex justify-end">
          <Button onClick={() => setEditing('new')} className="bg-[#0F172A] text-white text-xs gap-1" data-testid="create-template-btn"><Plus className="w-3.5 h-3.5" /> Nueva plantilla</Button>
        </div>
      )}

      {!marketingEmbed && (
      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {[{k:'templates',l:'Plantillas'},{k:'dominio',l:'Mi dominio'},{k:'config',l:'Configuración'},{k:'logs',l:'Historial'},{k:'suppressions',l:'Bajas'}].map(t=>(
          <button key={t.k} onClick={()=>{ setTab(t.k); onTabChange?.(t.k); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 ${tab===t.k?'border-[#F72585] text-[#0F172A]':'border-transparent text-[#64748B]'}`} data-testid={`tab-${t.k}`}>{t.l}</button>
        ))}
      </div>
      )}

      {tab === 'templates' && (
        <div className="space-y-5" data-testid="email-templates-simplified">
          {/* Modelo mental en 10 segundos */}
          <div className="rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] p-4 space-y-3" data-testid="templates-howto">
            <div>
              <h3 className="text-sm font-bold text-[#0F172A]">Plantilla = el diseño del correo</h3>
              <p className="text-xs text-[#64748B] mt-1">
                Aquí solo defines <strong>cómo se ve</strong> el mensaje (textos, colores, logo).
                No es WhatsApp. No es la IA. Es el mail que sale con tu marca.
              </p>
            </div>
            <ol className="grid sm:grid-cols-3 gap-2 text-xs">
              <li className="rounded-xl border border-[#E2E8F0] bg-white p-3">
                <span className="text-[10px] font-bold text-[#F72585]">1 · DISEÑO</span>
                <p className="mt-1 text-[#0F172A] font-medium">Elige un diseño o crea uno</p>
                <p className="text-[#94A3B8] mt-0.5">Galería o «Nueva plantilla»</p>
              </li>
              <li className="rounded-xl border border-[#E2E8F0] bg-white p-3">
                <span className="text-[10px] font-bold text-[#F72585]">2 · A MUCHOS</span>
                <p className="mt-1 text-[#0F172A] font-medium">Envío masivo</p>
                <p className="text-[#94A3B8] mt-0.5">
                  <button type="button" className="underline text-[#0F172A]" onClick={() => navigate('/tenant/email/nuevo')}>
                    Ir a Envíos
                  </button>
                  {' '}· tú eliges la lista
                </p>
              </li>
              <li className="rounded-xl border border-[#E2E8F0] bg-white p-3">
                <span className="text-[10px] font-bold text-[#F72585]">3 · SOLO</span>
                <p className="mt-1 text-[#0F172A] font-medium">Automático (cumple, cita…)</p>
                <p className="text-[#94A3B8] mt-0.5">
                  <button type="button" className="underline text-[#0F172A]" onClick={() => navigate('/tenant/email/automaticos')}>
                    Ir a Automatizaciones
                  </button>
                  {' '}· el sistema manda
                </p>
              </li>
            </ol>
          </div>

          {/* Galería */}
          <div className="space-y-3" data-testid="email-stock-gallery">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A]">Diseños listos</h3>
                <p className="text-xs text-[#64748B]">Un clic para copiarlo a tu negocio y editarlo. Tu logo se pone al enviar.</p>
              </div>
              <Button onClick={() => setEditing('new')} className="bg-[#0F172A] text-white text-xs gap-1 h-8" data-testid="create-template-btn-inline">
                <Plus className="w-3.5 h-3.5" /> En blanco
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {stock.map(s => {
                const auto = STOCK_AUTO[s.key];
                return (
                  <div key={s.key} className="border border-[#E2E8F0] rounded-xl p-3 bg-white flex flex-col gap-2" data-testid={`stock-${s.key}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                        <p className="text-[11px] text-[#94A3B8] line-clamp-2">{s.preview || s.subject}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-[#64748B] bg-[#F1F5F9] px-1.5 py-0.5 rounded shrink-0">{s.category || 'email'}</span>
                    </div>
                    <div className="mt-auto flex flex-col gap-1.5">
                      <button type="button" disabled={cloning===s.key} onClick={() => openStock(s)}
                        className="w-full text-xs font-semibold rounded-lg bg-[#0F172A] text-white px-3 py-2 hover:bg-[#1E293B] disabled:opacity-50"
                        data-testid={`clone-stock-${s.key}`}>
                        {cloning===s.key ? 'Abriendo…' : (findExistingStockCopy(s) ? 'Editar mi copia' : 'Usar y editar diseño')}
                      </button>
                      {auto && (
                        <button type="button" onClick={() => navigate(auto.path)}
                          className="w-full text-[11px] font-medium rounded-lg border border-[#F72585]/30 text-[#F72585] bg-[#FFF5F9] px-3 py-1.5 hover:bg-[#FFE4F0]"
                          data-testid={`stock-auto-${s.key}`}>
                          Activar envío automático → {auto.label}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mis diseños */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[#0F172A]">{filter === 'archived' ? 'Archivadas' : 'Mis diseños'}</h3>
                <p className="text-xs text-[#64748B]">
                  {filter === 'archived'
                    ? 'Puedes restaurarlas como borrador cuando quieras.'
                    : 'Los que ya son tuyos. Edítalos o úsalos en un envío / automático.'}
                </p>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="relative w-40">
                  <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[#94A3B8]" />
                  <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar…" className="pl-8 text-xs h-9" />
                </div>
                {[['','Todas'],['draft','Borrador'],['active','Listas'],['archived','Archivadas']].map(([f,l])=>(
                  <button key={f||'all'} type="button" onClick={()=>setFilter(f)}
                    className={`px-2.5 py-1 text-[11px] rounded-full border ${filter===f?'bg-[#0F172A] text-white border-[#0F172A]':'border-[#E2E8F0] text-[#64748B]'}`}
                    data-testid={`filter-${f||'all'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {(() => {
              const mine = filtered;
              const statusLabel = (s) => (s === 'active' ? 'Lista para usar' : s === 'draft' ? 'Borrador' : s === 'archived' ? 'Archivada' : s);
              const row = (t) => (
                <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-[#CBD5E1] transition-colors" data-testid={`template-${t.key}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-[#64748B]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A] truncate">{t.name}</p>
                      <p className="text-[11px] text-[#94A3B8] truncate">{t.subject || t.key}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status]||''}`}>
                          {statusLabel(t.status)}
                        </span>
                        <OriginBadge t={t} />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    {t.status === 'archived' ? (
                      <button type="button"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/tenant/email/templates/${t.id}/restore`);
                            toast.success('Restaurada como borrador');
                            if (filter === 'archived') setTemplates(p => p.filter(x => x.id !== t.id));
                            else setTemplates(p => p.map(x => x.id === t.id ? data : x));
                          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        }}
                        className="text-[11px] font-semibold rounded-full bg-[#0F172A] text-white px-3 py-1.5"
                        data-testid={`restore-${t.key}`}>
                        Restaurar
                      </button>
                    ) : (
                      <>
                        <button type="button" onClick={()=>setEditing(t)}
                          className="text-[11px] font-semibold rounded-full bg-[#0F172A] text-white px-3 py-1.5" data-testid={`edit-${t.key}`}>
                          Editar diseño
                        </button>
                        <button type="button" onClick={()=>setPreview(t)}
                          className="text-[11px] font-medium rounded-full border border-[#E2E8F0] px-3 py-1.5 text-[#64748B]">
                          Ver
                        </button>
                        <button type="button" onClick={()=>setTestModal(t)}
                          className="text-[11px] font-medium rounded-full border border-[#E2E8F0] px-3 py-1.5 text-[#64748B]">
                          Probar
                        </button>
                        <button type="button" onClick={() => navigate('/tenant/email/nuevo')}
                          className="text-[11px] font-medium rounded-full border border-[#E2E8F0] px-3 py-1.5 text-[#64748B]">
                          Enviar a muchos
                        </button>
                        <button type="button" onClick={() => navigate('/tenant/email/automaticos')}
                          className="text-[11px] font-medium rounded-full border border-[#F72585]/30 bg-[#FFF5F9] text-[#F72585] px-3 py-1.5">
                          Automático
                        </button>
                        <button type="button" onClick={async()=>{
                          try{
                            await api.delete(`/tenant/email/templates/${t.id}`);
                            setTemplates(p=>p.filter(x=>x.id!==t.id));
                            toast.success('Archivada — la ves en «Archivadas»');
                          }catch(e){toast.error(formatApiError(e.response?.data?.detail));}
                        }}
                          className="text-[11px] rounded-full px-2 py-1.5 text-[#94A3B8] hover:text-red-500" title="Archivar"
                          data-testid={`archive-${t.key}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
              return (
                <div className="space-y-4">
                  {mine.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-[#E2E8F0] rounded-xl bg-white">
                      <p className="text-sm text-[#64748B]">
                        {filter === 'archived' ? 'No hay plantillas archivadas.' : 'Aún no tienes diseños propios.'}
                      </p>
                      {filter !== 'archived' && (
                        <p className="text-xs text-[#94A3B8] mt-1">Empieza con un diseño listo de arriba → «Usar y editar diseño».</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="templates-campaign">{mine.map(row)}</div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {tab === 'dominio' && <SesDominioPanel />}
      {tab === 'config' && <ConfigPanel config={config} setConfig={setConfig} />}
      {tab === 'logs' && <LogsPanel logs={logs} />}
      {tab === 'suppressions' && <SuppressionsPanel />}
    </div>
  );
}

function ConfigPanel({ config, setConfig }) {
  const [form, setForm] = useState(config || {});
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get('/tenant/email/providers').then(r => setProviders(r.data || [])).catch(() => {});
  }, []);

  const configuredProviders = providers.filter(p => p.connection_status === 'configured' && p.status === 'active');
  const canTest = configuredProviders.length > 0;

  const handleTestSend = async () => {
    const to = (testEmail || '').trim();
    if (!to) { toast.error('Escribe el correo donde quieres recibir la prueba'); return; }
    setTestSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/tenant/email/test', {
        to_email: to,
        provider_key: form.default_provider_key || null,
        from_email: form.default_from_email || null,
        from_name: form.default_from_name || null,
      });
      setTestResult(data);
      if (data.ok) toast.success('Correo de prueba enviado');
      else toast.error(data.error_message || 'No se pudo enviar el correo de prueba');
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || 'No se pudo enviar el correo de prueba';
      toast.error(msg);
      setTestResult({ ok: false, error_message: msg });
    } finally { setTestSending(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/tenant/email/config', {
        default_provider_key: form.default_provider_key || null,
        default_from_email: form.default_from_email || null,
        default_from_name: form.default_from_name || null,
        default_reply_to: form.default_reply_to || null,
        environment: form.environment || 'sandbox',
      });
      setConfig(data); toast.success('Configuración guardada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-xl" data-testid="email-config-panel">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="email-config-info-banner">
        <AlertTriangle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-blue-700">
          <p className="font-medium mb-1">Las credenciales (API key, SMTP, etc.) se configuran en Integraciones.</p>
          <p>Aquí solo eliges el provider por defecto y el remitente.</p>
          <button onClick={() => navigate('/tenant/integrations?category=email')}
            className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 underline" data-testid="email-config-connect-cta">
            Ir a Integraciones → Email
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <h3 className="text-sm font-semibold text-[#0F172A]">Configuración Email</h3>

        {/* Provider selector */}
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Provider por defecto</label>
          {configuredProviders.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700" data-testid="email-no-providers-warning">
              No hay providers de email conectados. <button onClick={() => navigate('/tenant/integrations?category=email')} className="underline font-medium">Conectar en Integraciones</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <select value={form.default_provider_key || ''} onChange={e => setForm(f => ({...f, default_provider_key: e.target.value || null}))}
                className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 flex-1" data-testid="email-default-provider-select">
                <option value="">— Seleccionar —</option>
                {configuredProviders.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
              {form.default_provider_key && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium" data-testid="email-provider-status-badge">
                  Conectado
                </span>
              )}
            </div>
          )}
        </div>

        {/* Operational fields */}
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Email remitente</label>
          <Input value={form.default_from_email || ''} onChange={e => setForm(f => ({...f, default_from_email: e.target.value}))} placeholder="noreply@tusitio.com" className="text-xs" data-testid="config-default_from_email" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre remitente</label>
          <Input value={form.default_from_name || ''} onChange={e => setForm(f => ({...f, default_from_name: e.target.value}))} className="text-xs" data-testid="config-default_from_name" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Reply-To</label>
          <Input value={form.default_reply_to || ''} onChange={e => setForm(f => ({...f, default_reply_to: e.target.value}))} className="text-xs" data-testid="config-default_reply_to" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#64748B] mb-1 block">Entorno</label>
          <select value={form.environment || 'sandbox'} onChange={e => setForm(f => ({...f, environment: e.target.value}))}
            className="text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 w-full" data-testid="config-environment">
            <option value="sandbox">Sandbox</option>
            <option value="production">Producción</option>
          </select>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-[#0F172A] text-white text-xs" data-testid="save-config-btn">
          <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* Probar envío — correo de prueba sin necesidad de template */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-3" data-testid="email-quick-test-panel">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-[#0F172A]">Probar envío</h3>
          <HelpTooltip text="Envía un correo real de prueba usando el remitente y el proveedor de arriba, para confirmar que tus correos sí salen. Si falla, revisa las credenciales (contraseña o clave del proveedor) en Integraciones → Email." />
        </div>
        <p className="text-xs text-[#64748B]">
          No necesitas crear una plantilla para probar: enviamos un correo genérico con tu remitente actual.
        </p>

        {!canTest ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700" data-testid="email-quick-test-no-provider">
            Primero conecta un proveedor de correo para poder enviar la prueba.{' '}
            <button onClick={() => navigate('/tenant/integrations?category=email')} className="underline font-medium">Conectar en Integraciones</button>
          </div>
        ) : (
          <>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Correo donde quieres recibir la prueba</label>
              <div className="flex gap-2">
                <Input
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="text-xs flex-1"
                  data-testid="quick-test-to-email"
                />
                <Button onClick={handleTestSend} disabled={testSending} className="bg-[#F72585] text-white text-xs whitespace-nowrap" data-testid="send-quick-test-btn">
                  <Send className="w-3.5 h-3.5 mr-1" />{testSending ? 'Enviando...' : 'Enviar correo de prueba'}
                </Button>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                La prueba usa el remitente y el proveedor que ves arriba, aunque todavía no los hayas guardado.
              </p>
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg border ${testResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="quick-test-result">
                <div className="flex items-center gap-2">
                  {testResult.ok ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                  <span className="text-xs font-medium">{testResult.ok ? 'Correo enviado' : 'No se pudo enviar'}</span>
                </div>
                {testResult.provider_key && (
                  <p className="text-[10px] text-[#64748B] mt-1">Enviado con: {friendlyEmailProviderName(testResult.provider_key)}</p>
                )}
                {testResult.provider_message_id && (
                  <p className="text-[10px] text-[#64748B] mt-1">Folio del proveedor: {testResult.provider_message_id}</p>
                )}
                {typeof testResult.duration_ms === 'number' && (
                  <p className="text-[10px] text-[#94A3B8] mt-1">Tardó {testResult.duration_ms} ms</p>
                )}
                {testResult.error_message && (
                  <p className="text-[10px] text-red-600 mt-1">{testResult.error_message}</p>
                )}
                {!testResult.ok && (
                  <button onClick={() => navigate('/tenant/integrations?category=email')} className="mt-2 text-[10px] underline font-medium text-red-600" data-testid="quick-test-fix-credentials">
                    Revisar credenciales en Integraciones → Email
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LogsPanel({ logs }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="email-logs-panel">
      <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Últimos Envíos</h3>
      {logs.length===0?<p className="text-xs text-[#94A3B8] text-center py-4">Sin envíos</p>:(
        <table className="w-full text-xs"><thead><tr className="text-[#64748B] border-b border-[#E2E8F0]">
          <th className="text-left py-2">Provider</th><th className="text-left py-2">Template</th><th className="text-left py-2">Status</th><th className="text-right py-2">Duración</th><th className="text-right py-2">Fecha</th>
        </tr></thead><tbody>{logs.map(l=>(
          <tr key={l.id} className="border-b border-[#F1F5F9]">
            <td className="py-1.5">{friendlyEmailProviderName(l.provider_key)}</td><td className="py-1.5">{l.template_key||'—'}</td>
            <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${l.status==='sent'?'bg-emerald-50 text-emerald-600':'bg-red-50 text-red-600'}`}>{l.status}</span></td>
            <td className="text-right py-1.5">{l.duration_ms}ms</td>
            <td className="text-right py-1.5 text-[#94A3B8]">{l.created_at?new Date(l.created_at).toLocaleString('es-MX',{hour:'2-digit',minute:'2-digit'}):''}</td>
          </tr>
        ))}</tbody></table>
      )}
    </div>
  );
}

function TemplateEditor({ template, onBack, onSaved }) {
  const [form, setForm] = useState(template || { key:'', name:'', subject:'', html_body:'', text_body:'', status:'draft' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleSave = async () => {
    setSaving(true);
    try {
      if (template?.id) {
        const { data } = await api.put(`/tenant/email/templates/${template.id}`, form);
        onSaved(data);
      } else {
        const { data } = await api.post('/tenant/email/templates', form);
        onSaved(data);
      }
      toast.success('Template guardado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="template-editor">
      <button onClick={onBack} className="text-xs text-[#94A3B8] hover:text-[#0F172A] flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Volver</button>
      <h2 className="text-lg font-bold text-[#0F172A]">{template?.id?'Editar':'Nuevo'} Template</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Key (único)</label><Input value={form.key} onChange={e=>set('key',e.target.value)} className="text-xs" disabled={!!template?.id} data-testid="tpl-key"/></div>
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre</label><Input value={form.name} onChange={e=>set('name',e.target.value)} className="text-xs" data-testid="tpl-name"/></div>
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Subject</label><Input value={form.subject} onChange={e=>set('subject',e.target.value)} className="text-xs" data-testid="tpl-subject"/></div>
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">From Email</label><Input value={form.default_from_email||''} onChange={e=>set('default_from_email',e.target.value)} className="text-xs"/></div>
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Status</label>
            <select value={form.status} onChange={e=>set('status',e.target.value)} className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5 w-full">
              <option value="draft">Draft</option><option value="active">Active</option>
            </select>
          </div>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">HTML Body</label>
            <textarea value={form.html_body} onChange={e=>set('html_body',e.target.value)} rows={10} className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono" data-testid="tpl-html-body"/></div>
          <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Text Body (opcional)</label>
            <textarea value={form.text_body||''} onChange={e=>set('text_body',e.target.value)} rows={3} className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono"/></div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] text-[#64748B] font-semibold mb-1">Variables disponibles</p>
            <div className="flex gap-1 flex-wrap">{VAR_HINTS.map(v=>(<span key={v} className="text-[10px] bg-white border border-[#E2E8F0] rounded px-1.5 py-0.5 font-mono cursor-pointer hover:bg-[#F1F5F9]" onClick={()=>set('html_body',(form.html_body||'')+'{{'+v+'}}')}>{`{{${v}}}`}</span>))}</div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-3 border-t border-[#E2E8F0]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#0F172A] text-white text-xs" data-testid="save-template-btn"><Save className="w-3.5 h-3.5 mr-1"/>{saving?'Guardando...':'Guardar'}</Button>
        <Button variant="outline" onClick={onBack} className="text-xs"><X className="w-3.5 h-3.5 mr-1"/>Cancelar</Button>
      </div>
    </div>
  );
}

function PreviewPanel({ template, onBack }) {
  const [preview, setPreview] = useState(null);
  const [vars, setVars] = useState({});
  const [loading, setLoading] = useState(false);
  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tenant/email/templates/${template.id}/preview`, { variables: vars });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { doPreview(); }, []);
  return (
    <div className="space-y-4" data-testid="template-preview">
      <button onClick={onBack} className="text-xs text-[#94A3B8] hover:text-[#0F172A] flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Volver</button>
      <h2 className="text-lg font-bold text-[#0F172A]">Preview: {template.name}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#64748B]">Variables</p>
          {(template.variables||[]).map(v=>(<div key={v}><label className="text-[10px] text-[#94A3B8]">{v}</label><Input value={vars[v]||''} onChange={e=>setVars(p=>({...p,[v]:e.target.value}))} className="text-xs h-7"/></div>))}
          <Button size="sm" onClick={doPreview} disabled={loading} className="text-xs bg-[#0F172A]" data-testid="refresh-preview-btn"><Eye className="w-3 h-3 mr-1"/>Actualizar</Button>
        </div>
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3">
          {preview && (<>
            <div><p className="text-[10px] text-[#64748B]">Subject</p><p className="text-sm font-medium text-[#0F172A]">{preview.rendered_subject}</p></div>
            {preview.warnings?.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5"/><div>{preview.warnings.map((w,i)=>(<p key={i} className="text-[10px] text-amber-700">{w}</p>))}</div></div>}
            <div><p className="text-[10px] text-[#64748B]">HTML Preview</p><div className="border border-[#E2E8F0] rounded p-3 text-sm" dangerouslySetInnerHTML={{__html:preview.rendered_html_body}}/></div>
            {preview.rendered_text_body && <div><p className="text-[10px] text-[#64748B]">Text Body</p><pre className="text-xs bg-[#F8FAFC] p-2 rounded">{preview.rendered_text_body}</pre></div>}
            <div className="flex gap-4 text-[10px] text-[#94A3B8]">
              <span>Detectadas: {preview.variables_detected?.join(', ')||'ninguna'}</span>
              <span>Faltantes: {preview.variables_missing?.join(', ')||'ninguna'}</span>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

function TestSendPanel({ template, onBack }) {
  const [toEmail, setToEmail] = useState('');
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get('/tenant/email/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  // Quien envia lo dice el backend, resuelto con la MISMA funcion que usa el
  // envio real. Antes se adivinaba aqui cruzando el proveedor guardado en la
  // plantilla con la lista de conectados, y por eso un tenant que ya habia
  // desconectado SendGrid seguia viendo «SendGrid (no conectado)» y el boton
  // apagado, con el correo incluido en su plan listo para enviar.
  const quienEnvia = config?.quien_envia;
  const providerKey = quienEnvia?.key || template?.provider_key || config?.default_provider_key;
  const nombreProveedor = quienEnvia?.nombre || friendlyEmailProviderName(providerKey);
  const canSend = quienEnvia
    ? Boolean(quienEnvia.listo)
    : Boolean(providerKey && config?.connected_providers?.find(
        p => p.key === providerKey && p.connection_status === 'configured'));
  const providerConnected = canSend;

  const handleSend = async () => {
    if (!toEmail) { toast.error('Email requerido'); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/tenant/email/templates/${template.id}/test`, { to_email: toEmail, variables: vars });
      setResult(data);
      if (data.ok) toast.success('Email enviado'); else toast.error(data.error_message || 'Error al enviar');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setResult({ ok: false, error_message: formatApiError(e.response?.data?.detail) }); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-lg" data-testid="test-send-panel">
      <button onClick={onBack} className="text-xs text-[#94A3B8] hover:text-[#0F172A] flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Volver</button>
      <h2 className="text-lg font-bold text-[#0F172A]">Enviar prueba: {template.name}</h2>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        {/* Provider hint */}
        <div className="text-xs flex items-center gap-2" data-testid="test-send-provider-hint">
          <Mail className="w-3.5 h-3.5 text-[#94A3B8]" />
          {providerKey ? (
            <span>Envía por: <strong>{nombreProveedor}</strong> {providerConnected ? <span className="text-emerald-600">(listo)</span> : <span className="text-amber-600">(todavía no)</span>}</span>
          ) : (
            <span className="text-amber-600">Todavía no tienes por dónde enviar.</span>
          )}
        </div>
        {/* Warning if provider exists but not connected */}
        {providerKey && !providerConnected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700" data-testid="test-send-not-connected-warning">
            <p>{quienEnvia?.motivo || `El proveedor ${nombreProveedor} no está conectado. Conecta las credenciales en Integraciones para poder enviar.`}</p>
            <button onClick={() => navigate(quienEnvia?.motivo?.includes('Mi dominio') ? '/tenant/integrations/email' : '/tenant/integrations?category=email')} className="mt-1 underline font-medium">{quienEnvia?.motivo?.includes('Mi dominio') ? 'Ir a Correo → Mi dominio' : 'Ir a Integraciones → Email'}</button>
          </div>
        )}
        {!providerKey && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <p>Sin provider de email configurado.</p>
            <button onClick={() => navigate('/tenant/integrations?category=email')} className="mt-1 underline font-medium">Conectar en Integraciones</button>
          </div>
        )}
        <div><label className="text-xs font-medium text-[#64748B] mb-1 block">Email destino *</label><Input value={toEmail} onChange={e=>setToEmail(e.target.value)} placeholder="test@example.com" className="text-xs" data-testid="test-to-email"/></div>
        {(template.variables||[]).map(v=>(<div key={v}><label className="text-[10px] text-[#94A3B8]">{v}</label><Input value={vars[v]||''} onChange={e=>setVars(p=>({...p,[v]:e.target.value}))} className="text-xs h-7"/></div>))}
        <Button onClick={handleSend} disabled={sending || !canSend} className={`text-xs w-full ${canSend ? 'bg-[#F72585] text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-testid="send-test-btn"><Send className="w-3.5 h-3.5 mr-1"/>{sending?'Enviando...':'Enviar Prueba'}</Button>
        {result && (
          <div className={`p-3 rounded-lg ${result.ok?'bg-emerald-50 border-emerald-200':'bg-red-50 border-red-200'} border`}>
            <div className="flex items-center gap-2">{result.ok?<CheckCircle className="w-4 h-4 text-emerald-500"/>:<AlertTriangle className="w-4 h-4 text-red-500"/>}<span className="text-xs font-medium">{result.ok?'Enviado':'Error'}</span></div>
            {result.provider_message_id && <p className="text-[10px] text-[#64748B] mt-1">ID: {result.provider_message_id}</p>}
            {result.error_message && <p className="text-[10px] text-red-600 mt-1">{result.error_message}</p>}
          </div>
        )}
      </div>
    </div>
  );
}


function SuppressionsPanel() {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('manually_blocked');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/email/suppressions').then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!email) { toast.error('Email requerido'); return; }
    try {
      const { data } = await api.post('/tenant/email/suppressions', { email, reason });
      setItems(prev => [data, ...prev]);
      setEmail('');
      toast.success('Suppression agregada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleToggle = async (id, newStatus) => {
    try {
      await api.patch(`/tenant/email/suppressions/${id}/status`, { status: newStatus });
      setItems(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
      toast.success(newStatus === 'removed' ? 'Suppression removida' : 'Suppression activada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="suppressions-panel">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[#0F172A]">Agregar Suppression</h3>
        <div className="flex gap-2">
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" className="text-xs flex-1" data-testid="suppression-email-input" />
          <select value={reason} onChange={e => setReason(e.target.value)} className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5">
            <option value="manually_blocked">Bloqueado manual</option>
            <option value="bounced">Bounce</option>
            <option value="complained">Complaint</option>
            <option value="unsubscribed">Desuscrito</option>
          </select>
          <Button onClick={handleAdd} className="bg-[#0F172A] text-white text-xs" data-testid="add-suppression-btn">Agregar</Button>
        </div>
      </div>
      {items.length === 0 ? <p className="text-xs text-[#94A3B8] text-center py-4">Sin suppressions</p> : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-[#F8FAFC] text-[#64748B] border-b border-[#E2E8F0]">
              <th className="text-left py-2.5 px-4">Email (masked)</th>
              <th className="text-left py-2.5 px-4">Razón</th>
              <th className="text-center py-2.5 px-4">Estado</th>
              <th className="text-right py-2.5 px-4"></th>
            </tr></thead>
            <tbody>{items.map(s => (
              <tr key={s.id} className="border-b border-[#F1F5F9]">
                <td className="py-2 px-4 font-mono">{s.email_normalized || s.email_hash?.slice(0,12) + '...'}</td>
                <td className="py-2 px-4">{s.reason}</td>
                <td className="py-2 px-4 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] ${s.status === 'active' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>{s.status}</span></td>
                <td className="py-2 px-4 text-right">
                  <button onClick={() => handleToggle(s.id, s.status === 'active' ? 'removed' : 'active')} className="text-xs text-[#64748B] hover:text-[#0F172A]">
                    {s.status === 'active' ? 'Remover' : 'Activar'}
                  </button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
